import { Form, InputRef } from 'antd';
import Box from 'components/Box';
import Button from 'components/Button';
import Modal from 'components/Modal';
import { NumberPadIntV2 } from 'components/NumberKeyBoard/Currency/NumberPadV2';
import Text from 'components/Text';
import { ICashDepositItem } from 'features/cashier/components/CashDeposit/types';
import cashierActions from 'features/cashier/services/actions';
import cashierApis from 'features/cashier/services/apis';
import { IApiUpdateCashDeposit, IApiUpdateCashDepositItem, ICashDepositResponseData } from 'features/cashier/services/types/api';
import { delay, get, set, sumBy } from 'lodash';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState, useTransition } from 'react';
import { IResponseDataBody } from 'services/response';
import { useSetLoadingPage } from 'services/UI/LoadingPage';
import { useAppDispatch } from 'store/hooks';
import styled, { css } from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import toast from 'utils/toast';
import CashDepositNote from './CashDepositNote';
import CashTypeItem, { CASH_DEPOSIT_CASH_TYPE, cashDepositInitialValues } from './CashTypeItem';
import { useNavigate } from 'react-router-dom';
import storage from 'utils/sessionStorage';
import { useReactToPrint } from 'react-to-print';
import CashDepositPrintPreview from './CashDepositPrintPreview';

const FormStyled = styled(Form)`
  ${({ disabled }) => disabled && css`
    pointer-events: none;
  `}
`;

type Props = {
  detailMode?: boolean;
};
type Ref = {
  open: (staffId: string) => void;
  detail: (cashDepositId: string, note?: string) => void;
};

export const useModalCashDepositRef = () => useRef<Ref>(null);
const ModalCashDeposit = forwardRef<Ref, Props>(({ detailMode = false }, ref) => {
  const navigate = useNavigate();
  const inputRef = useRef<InputRef>(null);
  const dispatch = useAppDispatch();
  const setPageLoading = useSetLoadingPage();
  const [form] = Form.useForm();
  const [open, setOpen] = useState(false);
  const [KBActive, setKBActive] = useState<CASH_DEPOSIT_CASH_TYPE>(CASH_DEPOSIT_CASH_TYPE.ONE_CENT);
  const [, startTransition] = useTransition();
  const [staffId, setStaffId] = useState<string>('');
  const [printData, setPrintData] = useState<ICashDepositResponseData | null>(null);
  const printRef = useRef<HTMLDivElement>(null);
  const onClose = () => startTransition(() => {
    setOpen(false);
    setKBActive(CASH_DEPOSIT_CASH_TYPE.ONE_CENT);
  });
  const onClear = () => {
    setKBActive(CASH_DEPOSIT_CASH_TYPE.ONE_CENT);
    form.resetFields();
    delay(() => inputRef.current?.focus(), 100);
  };

  const onReport = () => {
    navigate(`/store/${storage.shop_id.get()}/report/cash-deposit`);
  };

  const onPrint = useReactToPrint({
    content: () => printRef.current,
  });

  useEffect(() => {
    if (printData) {
      setTimeout(() => {
        onPrint();
        setPrintData(null);
      }, 100);
    }
  }, [printData, onPrint]);

  const onOpen: Ref['open'] = (_staffId) => {
    form.resetFields();
    setStaffId(_staffId);
    form.setFieldsValue(cashDepositInitialValues);
    startTransition(() => {
      setOpen(true);
      setKBActive(CASH_DEPOSIT_CASH_TYPE.ONE_CENT);
    });
    delay(() => inputRef.current?.focus(), 500);
  };

  const detail: Ref['detail'] = async (detailId, note) => {
    form.resetFields();
    setPageLoading(true);
    try {
      const res: IResponseDataBody<ICashDepositItem[]> = await cashierApis.getCashDepositDetail(detailId);
      const cashDeposits = res.data.data;
      if (cashDeposits) {
        const obj = {
          ...cashDepositInitialValues,
        };
        cashDeposits.map(o => {
          const config = get(cashDepositInitialValues, o.cashType);
          if (!config) return;
          set(obj, o.cashType, { ...config, ...o });
        });
        form.setFieldsValue(obj);
        form.setFieldValue('note', note || '');
        startTransition(() => {
          setOpen(true);
          setKBActive(CASH_DEPOSIT_CASH_TYPE.ONE_CENT);
        });
      }
    } catch (error) {
    } finally {
      setPageLoading(false);
    }
  };

  const handleFinish = async (values: any) => {
    const data = values as Record<CASH_DEPOSIT_CASH_TYPE, ICashDepositItem>;
    const note = values.note as string;
    // @ts-ignore
    delete (data as any).note;

    setPageLoading(true);
    const cashDeposits: IApiUpdateCashDepositItem[] = Object.entries(data).map(([key, item]) => ({
      cashType: key,
      quantity: item.quantity,
      total: (item.quantity || 0) * (item.value || 0),
    } as IApiUpdateCashDepositItem));

    const body: IApiUpdateCashDeposit = {
      staffId: staffId,
      note,
      cashDeposits: cashDeposits,
    };
    try {
      const res: IResponseDataBody<ICashDepositResponseData> = await cashierApis.updateCashDeposit(body);
      if (res.data.data) {
        toast.success('Success');
        dispatch(cashierActions.getCashDeposit.fetch());
        setOpen(false);
        setPrintData(res.data.data);
      }
    } catch (error) {
      const msg = get(error, 'response.data.message');
      toast.error(msg ?? 'Fail!');
    } finally {
      setPageLoading(false);
    }
  };

  const onKBFocus = (type: CASH_DEPOSIT_CASH_TYPE) => () => {
    setKBActive(type);
  };

  useImperativeHandle(ref, () => ({
    open: onOpen,
    detail,
  }));

  return (
    <>
      <Modal
        v2
        footer={detailMode ? undefined : null}
        modalTitle='CASH DEPOSIT'
        visible={open}
        onClose={onClose} width={'auto'}
        noneBodyStyle
        noneFooterStyle
        hiddenOkButton={detailMode}
      >
        <FormStyled disabled={detailMode} form={form} onFinish={handleFinish} initialValues={cashDepositInitialValues}>
          <Box display='flex' gap='2'>
            <Box>
              <Box display='flex' flexWrap='nowrap'>
                {Object.keys(CASH_DEPOSIT_CASH_TYPE).map((type) => (
                  <Form.Item name={type} noStyle key={type}>
                    <CashTypeItem
                      form={form}
                      inputRef={KBActive === type ? inputRef : undefined}
                      type={type as CASH_DEPOSIT_CASH_TYPE}
                      onFocus={onKBFocus(type as CASH_DEPOSIT_CASH_TYPE)}
                      disabled={detailMode}
                    />
                  </Form.Item>
                ))}
              </Box>
              <TotalBox>
                <p className='total-label' >TOTAL:</p>
                <Form.Item shouldUpdate noStyle>
                  {({ getFieldsValue }) => {
                    const values = getFieldsValue();
                    if (values.note) {
                      delete values.note;
                    }
                    // @ts-ignore
                    const arr: ICashDepositItem[] = Object.values(values).filter(Boolean);
                    return <Text className='total-value' >{formatCurrency(sumBy(arr, o => (o?.value || 0) * o?.quantity))}</Text>;
                  }}
                </Form.Item>
              </TotalBox>
              <Form.Item noStyle name={[KBActive, 'quantity']}>
                <NumberPadIntV2 controlRef={inputRef} />
              </Form.Item>
            </Box>
            <Box display='flex' flexDirection='column' gap='1' alignSelf='stretch'>
              <Form.Item name={'note'} noStyle>
                <CashDepositNote />
              </Form.Item>
            </Box>
          </Box>
          {!detailMode && <ModalActionsStyled>
            <Button cardCashier onClick={onClose} ntype='SECONDARY'><span>Close</span></Button>
            <Button cardCashier onClick={onClear} ntype='DANGER'><span>Clear</span></Button>
            <Button cardCashier onClick={onReport} ntype='GREEN'><span style={{ color: '#fff' }}>Report</span></Button>
            <Button cardCashier ntype='PRIMARY' htmlType='submit'><span>OK</span></Button>
          </ModalActionsStyled>}
        </FormStyled>
      </Modal>

      {printData && (
        <div style={{ display: 'none' }}>
          <PrintContainer ref={printRef}>
            <CashDepositPrintPreview data={printData} />
          </PrintContainer>
        </div>
      )}
    </>
  );
});
ModalCashDeposit.displayName = 'ModalCashDeposit';
export default ModalCashDeposit;
const ModalActionsStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  button.cardCashier {
    flex: 1;
    height: 64px !important;
    span {
      font-size: 20px;
    }
  }
  padding: 16px;
`;

const TotalBox = styled.div`
  display: flex;
  margin-top: 8px;
  padding: 0px 40px;
  justify-content: center;
  align-items: center;
  align-self: stretch;
  gap: 16px;
  border-bottom: 1px solid #86909C;
  background: #E8FBFF;

  .total-label {
    color: #1D2129;
    text-align: center;
    font-family: Poppins;
    font-size: 24px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }

  .total-value {
    color: #1D2129;
    text-align: center;
    font-family: Poppins;
    font-size: 56px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }
`;

const PrintContainer = styled.div`
  background: white;
`;